import { useEffect, useState } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import {
  reportMoney,
  reportMonthToDate,
  reportMonthWindow,
  reportNumber,
  runReport,
  type ReportResult,
} from '../lib/reports';

export interface CashDay {
  date: string;
  label: string;
  total: number;
  isToday: boolean;
}

export interface CashChannel {
  key: string;
  label: string;
  amount: number;
  count: number;
  credit: boolean;
}

export interface CashCustomer {
  name: string;
  amount: number;
  count: number;
}

export interface AgingBucket {
  key: string;
  label: string;
  amount: number;
  count: number;
}

export interface CashReport {
  loading: boolean;
  error: string | null;
  monthLabel: string;
  collected: number;
  credit: number;
  invoiced: number;
  outstanding: number;
  openInvoices: number;
  overdue: number;
  today: number;
  avgPerDay: number;
  projected: number;
  lastMonth: number;
  tips: number;
  surcharges: number;
  refunds: number;
  refundCount: number;
  net: number;
  days: CashDay[];
  channels: CashChannel[];
  customers: CashCustomer[];
  aging: AgingBucket[];
}

export interface LeadDay {
  date: string;
  label: string;
  open: number;
  won: number;
  lost: number;
  total: number;
  isToday: boolean;
}

export interface LeadSource {
  name: string;
  leads: number;
  won: number;
  value: number;
}

export interface LeadStage {
  name: string;
  open: number;
  value: number;
}

export interface LeadsReport {
  loading: boolean;
  error: string | null;
  monthLabel: string;
  leads: number;
  lastMonth: number;
  openNow: number;
  won: number;
  lost: number;
  opening: number;
  today: number;
  avgPerDay: number;
  projected: number;
  pipelineValue: number;
  days: LeadDay[];
  sources: LeadSource[];
  stages: LeadStage[];
}

export interface VelocityReport {
  loading: boolean;
  perDay: number;
  previousPerDay: number;
  leadsPerDay: number;
  leadsPerDayLastMonth: number;
  avgDeal: number;
  conversion: number;
  cohortLeads: number;
  cohortWon: number;
  trailingDeals: number;
}

const CREDIT_CHANNELS = new Set(['wallet', 'gift_card']);

const CHANNEL_LABELS: Record<string, string> = {
  cash: 'Cash',
  bank_transfer: 'Bank transfer',
  check: 'Check',
  debit: 'Debit',
  credit: 'Credit card',
  card_online: 'Card online',
  card_tap_to_pay: 'Tap to pay',
  card_reader: 'Card reader',
  gift_card: 'Gift card',
  wallet: 'Wallet',
  financing: 'Financing',
  other: 'Other',
};

const AGING_ORDER = ['current', '1-30', '31-60', '61-90', '90+'];
const AGING_LABELS: Record<string, string> = {
  current: 'Current',
  '1-30': '1–30 days',
  '31-60': '31–60 days',
  '61-90': '61–90 days',
  '90+': '90+ days',
};

const emptyCash: CashReport = {
  loading: true,
  error: null,
  monthLabel: '',
  collected: 0,
  credit: 0,
  invoiced: 0,
  outstanding: 0,
  openInvoices: 0,
  overdue: 0,
  today: 0,
  avgPerDay: 0,
  projected: 0,
  lastMonth: 0,
  tips: 0,
  surcharges: 0,
  refunds: 0,
  refundCount: 0,
  net: 0,
  days: [],
  channels: [],
  customers: [],
  aging: [],
};

const emptyLeads: LeadsReport = {
  loading: true,
  error: null,
  monthLabel: '',
  leads: 0,
  lastMonth: 0,
  openNow: 0,
  won: 0,
  lost: 0,
  opening: 0,
  today: 0,
  avgPerDay: 0,
  projected: 0,
  pipelineValue: 0,
  days: [],
  sources: [],
  stages: [],
};

const emptyVelocity: VelocityReport = {
  loading: true,
  perDay: 0,
  previousPerDay: 0,
  leadsPerDay: 0,
  leadsPerDayLastMonth: 0,
  avgDeal: 0,
  conversion: 0,
  cohortLeads: 0,
  cohortWon: 0,
  trailingDeals: 0,
};

function shiftDate(iso: string, days: number): string {
  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function eachDate(since: string, until: string): string[] {
  const dates: string[] = [];
  let cursor = since;
  while (cursor <= until && dates.length < 40) {
    dates.push(cursor);
    cursor = shiftDate(cursor, 1);
  }
  return dates;
}

export function dayLabel(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export function monthLabel(since: string): string {
  return new Date(`${since}T12:00:00Z`).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function channelLabel(key: string): string {
  return CHANNEL_LABELS[key] || key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function cashFromChannels(result: ReportResult): { collected: number; credit: number } {
  let collected = 0;
  let credit = 0;
  for (const row of result.rows) {
    const amount = reportMoney({ ...result, rows: [row] }, 'collected');
    if (CREDIT_CHANNELS.has(String(row.channel || ''))) credit += amount;
    else collected += amount;
  }
  return { collected, credit };
}

/** Cash collected, new leads, and sales velocity from the report datasets. */
export function useControlCentreReports(enabled: { money: boolean; leads: boolean }) {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const timeZone = selectedBusiness?.timezone || 'America/Vancouver';
  const [cash, setCash] = useState<CashReport>(emptyCash);
  const [leads, setLeads] = useState<LeadsReport>(emptyLeads);
  const [velocity, setVelocity] = useState<VelocityReport>(emptyVelocity);

  useEffect(() => {
    if (!businessId || (!enabled.money && !enabled.leads)) {
      setCash({ ...emptyCash, loading: false });
      setLeads({ ...emptyLeads, loading: false });
      setVelocity({ ...emptyVelocity, loading: false });
      return;
    }

    let cancelled = false;
    const mtd = reportMonthToDate(timeZone, 0);
    const lastMtd = reportMonthToDate(timeZone, -1);
    const lastMonth = reportMonthWindow(timeZone, -1);
    const today = mtd.until;
    const daysElapsed = Number(today.slice(8, 10));
    const daysInMonth = Number(reportMonthWindow(timeZone, 0).until.slice(8, 10));
    const daysLastMonth = Number(lastMonth.until.slice(8, 10));
    const label = monthLabel(mtd.since);

    const load = () => {
      if (enabled.money) {
        setCash((current) => ({ ...current, loading: current.days.length === 0, error: null }));
        void Promise.all([
          runReport(businessId, `FROM payments SHOW collected, tips, surcharges, refunds, refund_count, net_collected SINCE ${mtd.since} UNTIL ${mtd.until}`),
          runReport(businessId, `FROM payments SHOW collected, payments GROUP BY channel SINCE ${mtd.since} UNTIL ${mtd.until}`),
          runReport(businessId, `FROM payments SHOW collected, payments GROUP BY channel SINCE ${lastMtd.since} UNTIL ${lastMtd.until}`),
          runReport(businessId, `FROM payments SHOW collected GROUP BY day SINCE ${mtd.since} UNTIL ${mtd.until}`),
          runReport(businessId, `FROM payments SHOW collected, payments GROUP BY customer SINCE ${mtd.since} UNTIL ${mtd.until}`),
          // Billed including tax, so it lines up with collected and outstanding (both tax-inclusive).
          runReport(businessId, `FROM sales SHOW total_invoiced SINCE ${mtd.since} UNTIL ${mtd.until}`),
          runReport(businessId, `FROM receivables SHOW outstanding, open_invoices, overdue AS OF ${today}`),
          runReport(businessId, `FROM receivables SHOW outstanding, open_invoices GROUP BY aging AS OF ${today}`),
        ])
          .then(([totals, channels, lastChannels, days, customers, sales, owing, aging]) => {
            if (cancelled) return;
            const split = cashFromChannels(channels);
            const previous = cashFromChannels(lastChannels);
            const byDate = new Map(days.rows.map((row) => [String(row.day), reportMoney({ ...days, rows: [row] }, 'collected')]));
            const filled = eachDate(mtd.since, today).map((date) => ({
              date,
              label: dayLabel(date),
              total: byDate.get(date) || 0,
              isToday: date === today,
            }));
            const agingRows = AGING_ORDER.map((key) => {
              const row = aging.rows.find((item) => item.aging === key);
              return {
                key,
                label: AGING_LABELS[key] || key,
                amount: row ? reportMoney({ ...aging, rows: [row] }, 'outstanding') : 0,
                count: row ? reportNumber({ ...aging, rows: [row] }, 'open_invoices') : 0,
              };
            });
            setCash({
              loading: false,
              error: null,
              monthLabel: label,
              collected: split.collected,
              credit: split.credit,
              invoiced: reportMoney(sales, 'total_invoiced'),
              outstanding: reportMoney(owing, 'outstanding'),
              openInvoices: reportNumber(owing, 'open_invoices'),
              overdue: reportMoney(owing, 'overdue'),
              today: byDate.get(today) || 0,
              avgPerDay: daysElapsed > 0 ? split.collected / daysElapsed : 0,
              projected: daysElapsed > 0 ? (split.collected / daysElapsed) * daysInMonth : 0,
              lastMonth: previous.collected,
              tips: reportMoney(totals, 'tips'),
              surcharges: reportMoney(totals, 'surcharges'),
              refunds: reportMoney(totals, 'refunds'),
              refundCount: reportNumber(totals, 'refund_count'),
              net: reportMoney(totals, 'net_collected'),
              days: filled,
              channels: channels.rows
                .map((row) => ({
                  key: String(row.channel || 'other'),
                  label: channelLabel(String(row.channel || 'other')),
                  amount: reportMoney({ ...channels, rows: [row] }, 'collected'),
                  count: reportNumber({ ...channels, rows: [row] }, 'payments'),
                  credit: CREDIT_CHANNELS.has(String(row.channel || '')),
                }))
                .filter((row) => row.amount > 0 || row.count > 0)
                .sort((a, b) => Number(a.credit) - Number(b.credit) || b.amount - a.amount),
              customers: customers.rows
                .map((row) => ({
                  name: String(row.customer || 'Unknown'),
                  amount: reportMoney({ ...customers, rows: [row] }, 'collected'),
                  count: reportNumber({ ...customers, rows: [row] }, 'payments'),
                }))
                .filter((row) => row.amount > 0)
                .sort((a, b) => b.amount - a.amount)
                .slice(0, 12),
              aging: agingRows,
            });
          })
          .catch((err: unknown) => {
            console.error('Cash collected report:', err);
            if (!cancelled) {
              setCash({
                ...emptyCash,
                loading: false,
                monthLabel: label,
                error: err instanceof Error ? err.message : 'Could not load cash collected',
              });
            }
          });
      } else {
        setCash({ ...emptyCash, loading: false });
      }

      if (enabled.leads) {
        setLeads((current) => ({ ...current, loading: current.days.length === 0, error: null }));
        setVelocity((current) => ({ ...current, loading: current.perDay === 0 }));
        const maturedSince = shiftDate(today, -90);
        const maturedUntil = shiftDate(today, -30);
        const trailingSince = shiftDate(today, -180);
        void Promise.all([
          runReport(businessId, `FROM leads SHOW leads, pipeline_value, open_now, opening_balance, deals_won, deals_lost SINCE ${mtd.since} UNTIL ${today}`),
          runReport(businessId, `FROM leads SHOW leads SINCE ${lastMonth.since} UNTIL ${lastMonth.until}`),
          runReport(businessId, `FROM leads SHOW leads, open, won, lost GROUP BY day SINCE ${mtd.since} UNTIL ${today}`),
          runReport(businessId, `FROM leads SHOW leads, won, pipeline_value GROUP BY source SINCE ${mtd.since} UNTIL ${today}`),
          runReport(businessId, `FROM leads SHOW open_now, pipeline_value GROUP BY stage SINCE ${mtd.since} UNTIL ${today}`),
          runReport(businessId, `FROM leads SHOW leads, won, conversion_rate SINCE ${maturedSince} UNTIL ${maturedUntil}`),
          runReport(businessId, `FROM leads SHOW average_won_value, deals_won SINCE ${trailingSince} UNTIL ${today}`),
        ])
          .then(([month, previous, days, sources, stages, matured, trailing]) => {
            if (cancelled) return;
            const newLeads = reportNumber(month, 'leads');
            const lastLeads = reportNumber(previous, 'leads');
            const leadsPerDay = newLeads / Math.max(daysElapsed, 1);
            const leadsPerDayLast = lastLeads / Math.max(daysLastMonth, 1);
            const avgDeal = reportMoney(trailing, 'average_won_value');
            const conversion = reportNumber(matured, 'conversion_rate');
            const rate = conversion / 100;
            const byDate = new Map(days.rows.map((row) => [String(row.day), row]));
            setLeads({
              loading: false,
              error: null,
              monthLabel: label,
              leads: newLeads,
              lastMonth: lastLeads,
              openNow: reportNumber(month, 'open_now'),
              won: reportNumber(month, 'deals_won'),
              lost: reportNumber(month, 'deals_lost'),
              opening: reportNumber(month, 'opening_balance'),
              today: byDate.has(today) ? reportNumber({ ...days, rows: [byDate.get(today)!] }, 'leads') : 0,
              avgPerDay: leadsPerDay,
              projected: leadsPerDay * daysInMonth,
              pipelineValue: reportMoney(month, 'pipeline_value'),
              days: eachDate(mtd.since, today).map((date) => {
                const row = byDate.get(date);
                const picked = row ? { ...days, rows: [row] } : null;
                const open = picked ? reportNumber(picked, 'open') : 0;
                const won = picked ? reportNumber(picked, 'won') : 0;
                const lost = picked ? reportNumber(picked, 'lost') : 0;
                return {
                  date,
                  label: dayLabel(date),
                  open,
                  won,
                  lost,
                  total: open + won + lost,
                  isToday: date === today,
                };
              }),
              sources: sources.rows
                .map((row) => ({
                  name: String(row.source || 'Unknown'),
                  leads: reportNumber({ ...sources, rows: [row] }, 'leads'),
                  won: reportNumber({ ...sources, rows: [row] }, 'won'),
                  value: reportMoney({ ...sources, rows: [row] }, 'pipeline_value'),
                }))
                .sort((a, b) => b.leads - a.leads || a.name.localeCompare(b.name)),
              stages: stages.rows
                .map((row) => ({
                  name: String(row.stage || 'Unstaged'),
                  open: reportNumber({ ...stages, rows: [row] }, 'open_now'),
                  value: reportMoney({ ...stages, rows: [row] }, 'pipeline_value'),
                }))
                .sort((a, b) => b.open - a.open || a.name.localeCompare(b.name)),
            });
            setVelocity({
              loading: false,
              perDay: leadsPerDay * avgDeal * rate,
              previousPerDay: leadsPerDayLast * avgDeal * rate,
              leadsPerDay,
              leadsPerDayLastMonth: leadsPerDayLast,
              avgDeal,
              conversion,
              cohortLeads: reportNumber(matured, 'leads'),
              cohortWon: reportNumber(matured, 'won'),
              trailingDeals: reportNumber(trailing, 'deals_won'),
            });
          })
          .catch((err: unknown) => {
            console.error('Leads report:', err);
            if (!cancelled) {
              const message = err instanceof Error ? err.message : 'Could not load leads';
              setLeads({ ...emptyLeads, loading: false, monthLabel: label, error: message });
              setVelocity({ ...emptyVelocity, loading: false });
            }
          });
      } else {
        setLeads({ ...emptyLeads, loading: false });
        setVelocity({ ...emptyVelocity, loading: false });
      }
    };

    load();
    const interval = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [businessId, timeZone, enabled.money, enabled.leads]);

  return { cash, leads, velocity };
}
